import { useState } from "react";
import { Plug, RefreshCw, Unplug } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useGet } from "../lib/hooks";
import { PageHeader, Pill } from "../components/Common";
import { RecordDialog } from "../components/RecordDialog";
import { Button } from "../components/ui/button";
import { XeroCard } from "../components/XeroCard";

export default function Integrations() {
  const [list, reload] = useGet("/integrations");
  const [logs, reloadLogs] = useGet("/sync-logs");
  const [connect, setConnect] = useState(null);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); reloadLogs(); } catch (e) { toast.error(errMsg(e)); } };
  const cats = [...new Set((list || []).map((i) => i.category))];
  return (
    <div>
      <PageHeader overline="Ecosystem" title="Integrations Hub" desc="Connect your business's own services. Cards marked Live are working now; Coming soon cards are being built." />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {cats.map((cat) => (
            <div key={cat}>
              <div className="overline mb-3">{cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.filter((i) => i.category === cat).map((i) => i.key === "xero" ? <XeroCard key="xero" /> : (
                  <div key={i.key} className="rounded-md border bg-card p-4 space-y-3" data-testid={`integration-${i.key}`}>
                    <div className="flex justify-between items-start">
                      <div><div className="font-display font-bold">{i.name}</div><div className="text-xs text-muted-foreground">{i.desc}</div></div>
                      <Pill tone={i.live ? "green" : i.status === "coming_soon" ? "amber" : i.status === "connected" ? "green" : "slate"} testid={`integration-status-${i.key}`}>{i.live ? "Live" : i.status === "coming_soon" ? "Coming soon" : i.status === "connected" ? "connected · mock" : i.status}</Pill>
                    </div>
                    {i.last_sync && <div className="text-[11px] font-mono text-muted-foreground">Last sync {fmtDate(i.last_sync)}</div>}
                    {!i.live && i.status !== "coming_soon" && (
                      <div className="flex gap-2">
                        {i.status !== "connected" ? (
                          <Button size="sm" onClick={() => setConnect(i)} data-testid={`connect-${i.key}`}><Plug className="h-3.5 w-3.5 mr-1" />Connect</Button>
                        ) : (
                          <>
                            <Button size="sm" variant="secondary" onClick={() => act(() => api.post(`/integrations/${i.key}/sync`), `${i.name} sync complete (mock)`)} data-testid={`sync-${i.key}`}><RefreshCw className="h-3.5 w-3.5 mr-1" />Sync now</Button>
                            <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/integrations/${i.key}/disconnect`), "Disconnected")} data-testid={`disconnect-${i.key}`}><Unplug className="h-3.5 w-3.5" /></Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-md border bg-card p-4 h-fit" data-testid="sync-log">
          <div className="overline mb-3">Sync log</div>
          {(logs || []).map((l) => <div key={l.id} className="border-b py-2 text-xs"><span className="text-primary font-mono">{l.provider}</span> {l.detail}<div className="text-muted-foreground">{fmtDate(l.created_at)}</div></div>)}
          {!logs?.length && <div className="text-sm text-muted-foreground">No sync activity yet.</div>}
        </div>
      </div>
      {connect && (
        <RecordDialog open onOpenChange={(o) => !o && setConnect(null)} title={`Connect ${connect.name}`}
          fields={(connect.fields || []).map((f) => ({ key: f, label: f.replace(/_/g, " "), type: /secret|token|key/.test(f) ? "password" : "text", full: true }))}
          initial={{}} onSave={(config) => act(() => api.post(`/integrations/${connect.key}/connect`, { config }), `${connect.name} connected`)} />
      )}
    </div>
  );
}
